
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { 
  MessageSquare, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  Plus
} from "lucide-react";

type AssistantRequest = {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'resolved' | 'in-progress';
  createdAt: string;
};

export const AssistantRequests = () => {
  const { t } = useTranslation();
  const [requests, setRequests] = useState<AssistantRequest[]>([
    {
      id: '1',
      title: 'Help with menu pricing strategy',
      description: 'Need assistance with optimizing our menu prices based on cost and market trends.',
      status: 'resolved',
      createdAt: '2023-11-15T10:30:00Z'
    },
    {
      id: '2',
      title: 'Issue with inventory tracking',
      description: 'The system is not correctly calculating inventory usage for some ingredients.',
      status: 'in-progress',
      createdAt: '2023-12-02T14:45:00Z'
    },
    {
      id: '3',
      title: 'Training for new staff',
      description: 'Would like to schedule a training session for new kitchen staff on using the recipe module.',
      status: 'pending',
      createdAt: '2023-12-10T09:15:00Z'
    }
  ]);
  
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [newRequest, setNewRequest] = useState({
    title: '',
    description: ''
  });

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'in-progress':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'resolved':
        return <CheckCircle2 className="h-4 w-4 mr-1" />;
      case 'in-progress':
        return <Clock className="h-4 w-4 mr-1" />;
      case 'pending':
      default:
        return <AlertCircle className="h-4 w-4 mr-1" />;
    }
  };

  const handleSubmitRequest = () => {
    if (newRequest.title.trim() && newRequest.description.trim()) {
      const request: AssistantRequest = {
        id: Date.now().toString(),
        title: newRequest.title,
        description: newRequest.description,
        status: 'pending',
        createdAt: new Date().toISOString()
      };
      
      setRequests([request, ...requests]);
      setNewRequest({ title: '', description: '' });
      setShowNewRequest(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            {t('settings.assistantRequests')}
          </CardTitle>
          <Button 
            size="sm" 
            onClick={() => setShowNewRequest(!showNewRequest)}
          >
            <Plus className="h-4 w-4 mr-1" />
            {t('settings.newRequest')}
          </Button>
        </div>
        <CardDescription>
          {t('settings.manageAssistantRequests')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {showNewRequest && (
          <div className="bg-muted/50 p-4 rounded-lg space-y-4 mb-6">
            <h3 className="font-medium text-lg">{t('settings.createNewRequest')}</h3>
            <div className="space-y-2">
              <Label htmlFor="requestTitle">{t('settings.requestTitle')}</Label>
              <Input 
                id="requestTitle" 
                value={newRequest.title}
                onChange={(e) => setNewRequest({...newRequest, title: e.target.value})}
                placeholder={t('settings.requestTitlePlaceholder') || 'Enter request title'}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="requestDescription">{t('settings.requestDescription')}</Label>
              <Textarea 
                id="requestDescription" 
                rows={4}
                value={newRequest.description}
                onChange={(e) => setNewRequest({...newRequest, description: e.target.value})}
                placeholder={t('settings.requestDescriptionPlaceholder') || 'Describe your request in detail'}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowNewRequest(false);
                  setNewRequest({ title: '', description: '' });
                }}
              >
                {t('settings.cancel')}
              </Button>
              <Button onClick={handleSubmitRequest}>{t('settings.submitRequest')}</Button>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {requests.length > 0 ? (
            requests.map((request) => (
              <div key={request.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-medium">{request.title}</h3>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium flex items-center ${getStatusBadgeClass(request.status)}`}>
                    {getStatusIcon(request.status)}
                    {t(`settings.${request.status}`)}
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-3">{request.description}</p>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">
                    {formatDate(request.createdAt)}
                  </span>
                  <Button size="sm" variant="outline">
                    {t('settings.viewDetails')}
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <MessageSquare className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
              <h3 className="font-medium">{t('settings.noRequests')}</h3>
              <p className="text-sm text-muted-foreground mt-1">{t('settings.createFirstRequest')}</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
